<template>
    <div class="modal fade" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div class="modal-content bg-olf-isabelline">
                <div class="modal-header">
                    <h1 class="heading-large mt-0">Accept Counter Offer</h1>
                    <button type="button" data-dismiss="modal" class="modal-close" aria-label="Cancel"
                        @click="closeModal()"><i class="fas fa-lg fa-times"></i></button>
                </div>
                <div v-if="!showPurchaseConfirmed">
                    <!-- Seller info -->
                    <div class="modal-body seller-info pt-0">
                        <h2 class="heading-medium" v-if="counterOffer.user">{{counterOffer.user.name}}</h2>
                        <span v-if="offersTimeRemaining[counterOffer._id] > 0" 
                            class="negotiation-countdown timer-displaying">
                            <span class="text-display">{{offersTimeRemaining[counterOffer._id] | countdownshortformat}}</span>
                            <radial-progress-bar class="countdown-progress" :diameter="35" 
                                :totalSteps="parseInt((new Date(counterOffer.expires) - new Date(counterOffer.created))/1000)" 
                                :completedSteps="offersTimeRemaining[counterOffer._id]" />
                        </span>
                        <span v-else class="negotiation-countdown">
                            <span v-if="new Date(counterOffer.expires) - new Date() > 1000">Updating...</span>
                            <span v-else class="text-danger">This offer has expired</span>
                        </span>
                    </div>

                    <!-- Order details -->
                    <div class="modal-body order-details bg-white">
                        <div class="row">
                            <div class="col-md-6 mb-2">
                                <label class="mb-0">Product</label>
                                <div>{{negotiationSelected.fuel_id | fueltype}}</div>
                            </div>
                            <div class="col-md-6 mb-2">
                                <label class="mb-0">Price (ppl)</label>
                                <div class="price" :class="{'changed': counterOffer.price !== initialCounterDetails.price}">{{counterOffer.price | priceformat}}</div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-md-6 mb-2">
                                <label class="mb-0">Seller</label>
                                <div v-if="counterOffer.user">{{counterOffer.user.account.name}}</div>
                                <div v-else>N/A</div>
                            </div>
                            <div class="col-md-6 mb-2">
                                <label class="mb-0">Volume/L</label>
                                <div :class="{'changed': counterOffer.quantity !== initialCounterDetails.quantity}">{{counterOffer.quantity | volumeformat}}</div>
                            </div>
                        </div>
                        <div v-if="negotiationType !== 'BUNKERING'" class="row">
                            <div v-if="negotiationType == 'SPOT'" class="col-md-6 mb-2">
                                <label class="mb-0">Terminal</label>
                                <div v-if="counterOffer.terminal">
                                    <span :class="{'changed': counterOffer.terminal._id !== initialCounterDetails.terminal._id}">{{counterOffer.terminal.name}}</span>
                                </div>
                                <div v-else>N/A</div>
                            </div>
                            <div class="col-md-6 mb-2">
                                <label class="mb-0">Lifting Period</label>
                                <div :class="{'changed': counterOffer.collectionTime !== initialCounterDetails.collectionTime}">{{counterOffer.collectionTime | collectiondaysformat}}</div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-md-6 mb-2">
                                <label class="mb-0">Measurement</label>
                                <div :class="{'changed': counterOffer.fuel_measurement_id !== initialCounterDetails.fuel_measurement_id}">{{counterOffer.fuel_measurement_id | fuelmeasurementtype}}</div>
                            </div>
                            <div class="col-md-6 mb-2">
                                <label class="mb-0">Payment Terms</label>
                                <div v-if="counterOffer.credit">{{counterOffer.credit.credit.days | creditdaysformat}}</div>
                                <div v-else>N/A</div>
                            </div>
                        </div>
                        <div class="row reference mt-3">
                            <template v-if="negotiationType !== 'BUNKERING'">
                                <div class="col-md-3">
                                    <label>Reference</label>
                                </div>
                                <div class="col-md-9">
                                    <input class="form-control no-focus" v-model="input.orderReference" 
                                        placeholder="e.g 12345ABCD"/>
                                </div>
                            </template>
                            <template v-else>
                                <div class="col-md-3">
                                    <label>Account No.</label>
                                </div>
                                <div class="col-md-9">
                                    <input class="form-control no-focus" v-model="input.orderReference" @keypress="isNumberKeyEvent($event)"/>
                                </div>
                            </template>
                        </div>
                    </div>

                    <!-- Total volume and price -->
                    <div class="modal-body total bg-olf-light-grey">
                        <div class="col-md-12">
                            <div class="row">
                                <div class="col-md-6">
                                    TOTAL
                                </div>
                                <div class="col-md-6">
                                    <span class="float-left" :class="{'changed': counterOffer.quantity !== initialCounterDetails.quantity}">
                                        {{counterOffer.quantity | volumeformat}} Litres
                                    </span>
                                    <span class="float-right" :class="{'changed': (counterOffer.quantity * counterOffer.price/100) !== (initialCounterDetails.quantity * initialCounterDetails.price/100)}">
                                        £{{counterOffer.quantity * counterOffer.price/100 | priceformat}}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Notes/comments -->
                    <div class="modal-footer d-flex justify-content-end">
                        <span class="error-message float-left" v-if="error">
                            <span>{{error}}</span>
                        </span>
                        <button class="btn btn-primary" :disabled="error || isPlacingOrder"
                            @click="confirmPurchase($event)">
                            <span v-if="isPlacingOrder">Processing...</span>
                            <span v-else>Confirm</span>
                        </button>
                    </div>
                </div>

                <!-- Confirmation screen after placing order -->
                <div v-else>
                    <div class="modal-body confirmation text-center">
                        <h1 class="heading-medium" >{{purchaseStatusMessage}}</h1>
                        <router-link to="/OrderHistory/all" class="btn btn-primary" tag="button" v-on:click.native="toPurchaseHistory">
                            Go to order history
                        </router-link>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { isNumberKeyEvent } from "@/util/util";

export default {
    data(){
        return {
            input: {
                orderReference: '',
                notes: ''
            },
            isPlacingOrder: false,
            initialCounterDetails: '',
            timeoutFunction: '',
            isNumberKeyEvent,
        }
    },
    created() {
        this.updateShowPurchaseConfirmed(false)
        this.input.orderReference = this.negotiationSelected.reference
        this.updateInitialCounterDetails()
    },
    computed: {
        ...mapState('negotiation', ['negotiationSelected', 'acceptedCounterId', 'offersTimeRemaining', 
            'showPurchaseConfirmed', 'purchaseStatusMessage']),
        ...mapState('fuelsType', ['fuels']),
        counterOffer: {
            get: function(){
                this.timeoutFunction = ''
                this.timeoutFunction = setTimeout(() => {
                    this.updateInitialCounterDetails()
                }, 5000)
                return this.negotiationSelected.counterOffers.find(offer => offer._id === this.acceptedCounterId);
            }
        },
        error: {
            get: function(){
                if (!(this.offersTimeRemaining[this.acceptedCounterId] > 0)){
                    return 'Counter offer has expired'
                }
                else if (this.negotiationType === "BUNKERING" && this.input.orderReference === "") {
                    return "Enter an Account No.";
                }
            }
        },
        negotiationType: {
            get: function(){
                if (this.fuels.length > 0 && this.negotiationSelected){
                    const fuel = this.fuels.find(fuel => fuel._id == this.negotiationSelected.fuel_id)
                    return fuel.type
                }
            }
        }
    },
    methods: {
        ...mapActions('negotiation', ['updateShowAcceptCounterOffer', 'updateShowPurchaseConfirmed', 'acceptCounterOfferConfirmed']),
        closeModal: function(){
            $(this.$el).modal('hide')
            this.updateShowPurchaseConfirmed(false)
            this.updateShowAcceptCounterOffer(false)
        },
        toPurchaseHistory: function(event){
            event.preventDefault()
            $(this.$el).modal('hide')
            this.updateShowPurchaseConfirmed(false)
            this.updateShowAcceptCounterOffer(false)
        },
        updateInitialCounterDetails: function(){
            this.initialCounterDetails = this.negotiationSelected.counterOffers.find(offer => offer._id === this.acceptedCounterId)
        },
        confirmPurchase: async function(event){
            this.isPlacingOrder = true

            const payload = {
                negotiation_id: this.negotiationSelected._id,
                counter_id: this.counterOffer._id,
                orderReference: this.input.orderReference
            }

            await this.acceptCounterOfferConfirmed(payload)
        }
    }
}
</script>
<style lang="scss" scoped>
@import "@/assets/scss/components/_modals.scss";
</style>